import React, { useEffect, useMemo, useState } from "react";
import {
  DefaultButton,
  Icon,
  IconButton,
  MessageBar,
  MessageBarType,
  Persona,
  PersonaSize,
  Pivot,
  PivotItem,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
} from "@fluentui/react";
import Button from "@mui/material/Button";
import { ReusableDialog } from "../../../components/popupModals/ReusableDialog";
import ConfirmModal from "../../../components/popupModals/ConfirmModal";
import { useTheme } from "../../../theme/OAS365Theme";
import { getFileIconPath } from "../../../functions/helperMethod";
import { copyTextToClipboard } from "../../../utils/copyTextToClipboard";

function resolveDocumentIconSrc(iconSrc, documentName) {
  if (iconSrc) return iconSrc;
  const name = documentName || "";
  const ext = name.includes(".") ? name.split(".").pop() : "";
  return getFileIconPath("", { FileExtension: ext });
}

function DocumentTypeIcon({ iconSrc, documentName, isShared, isFolder, theme }) {
  const resolvedSrc = isFolder
    ? ""
    : resolveDocumentIconSrc(iconSrc, documentName);

  return (
    <div
      style={{
        position: "relative",
        width: 28,
        height: 28,
        flexShrink: 0,
      }}
      aria-hidden="true"
    >
      {isFolder ? (
        <Icon
          iconName="FabricFolderFill"
          styles={{
            root: {
              fontSize: 22,
              width: 28,
              height: 28,
              lineHeight: "28px",
              textAlign: "center",
              color: "#FFCE44",
            },
          }}
        />
      ) : resolvedSrc ? (
        <img
          src={resolvedSrc}
          alt=""
          style={{
            width: 28,
            height: 28,
            display: "block",
            objectFit: "contain",
          }}
        />
      ) : (
        <Icon
          iconName="Page"
          styles={{
            root: {
              fontSize: 22,
              width: 28,
              height: 28,
              lineHeight: "28px",
              textAlign: "center",
              color: theme.colors.fontColor,
            },
          }}
        />
      )}
      {isShared ? (
        <span
          style={{
            position: "absolute",
            right: -2,
            bottom: -2,
            width: 14,
            height: 14,
            borderRadius: "50%",
            backgroundColor: "#ffffff",
            border: `1px solid ${theme.colors.dividerColor ?? "#E1DFDD"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.04)",
          }}
          title="Shared"
        >
          <Icon
            iconName="People"
            styles={{
              root: {
                fontSize: 9,
                color: "#605E5C",
                lineHeight: "9px",
              },
            }}
          />
        </span>
      ) : null}
    </div>
  );
}

function SharingPrincipalRow({
  name,
  secondaryText,
  role,
  theme,
  onChangeRole,
  isUpdating = false,
  disabled = false,
}) {
  const roleMenuProps = onChangeRole
    ? {
        items: [
          { key: "read", text: "Can view", iconProps: { iconName: "View" } },
          { key: "write", text: "Can edit", iconProps: { iconName: "Edit" } },
        ].map((item) => ({
          ...item,
          canCheck: true,
          checked: item.text === role,
          onClick: () => {
            if (item.text !== role) onChangeRole(item.key);
          },
        })),
        styles: {
          root: { backgroundColor: theme.colors.contextMenuBackground ?? "#fff" },
          subComponentStyles: {
            menuItem: {
              root: { backgroundColor: theme.colors.contextMenuBackground ?? "#fff" },
              rootHovered: { backgroundColor: theme.colors.contextMenuHover ?? "#E2EFDF" },
              label: { fontFamily: theme.fonts.regular, fontSize: 12, color: theme.colors.fontColor },
              icon: { color: theme.colors.fontColor },
              checkmarkIcon: { color: theme.colors.fontColor },
            },
          },
        },
      }
    : null;

  return (
    <Stack
      horizontal
      verticalAlign="center"
      tokens={{ childrenGap: 12 }}
      styles={{
        root: {
          padding: "10px 4px",
          borderBottom: `1px solid ${theme.colors.dividerColor ?? "#EDEBE9"}`,
        },
      }}
    >
      <Persona
        text={name}
        secondaryText={secondaryText}
        size={PersonaSize.size32}
        styles={{
          root: {
            flex: 1,
            minWidth: 0,
            // Fluent's Persona darkens primary/secondary text on root hover
            // (neutralDark), which turns black on the dark theme.
            selectors: {
              ":hover .ms-Persona-primaryText": {
                color: theme.colors.fontColor,
              },
              ":hover .ms-Persona-secondaryText": {
                color: theme.colors.deactiveColor,
              },
            },
          },
          primaryText: {
            fontFamily: theme.fonts.regular,
            fontSize: 13,
            color: theme.colors.fontColor,
            selectors: {
              ":hover": { color: theme.colors.fontColor },
            },
          },
          secondaryText: {
            fontFamily: theme.fonts.regular,
            fontSize: 11,
            color: theme.colors.deactiveColor,
            selectors: {
              ":hover": { color: theme.colors.deactiveColor },
            },
          },
        }}
      />
      {isUpdating ? (
        <Spinner size={SpinnerSize.small} />
      ) : roleMenuProps ? (
        <DefaultButton
          text={role}
          menuProps={roleMenuProps}
          disabled={disabled}
          ariaLabel={`Change access for ${name}`}
          styles={{
            root: {
              border: "none",
              background: "transparent",
              padding: "0 4px",
              minWidth: 0,
              height: 28,
              flexShrink: 0,
              color: theme.colors.deactiveColor,
            },
            rootHovered: { background: "transparent", color: theme.colors.fontColor },
            rootPressed: { background: "transparent", color: theme.colors.fontColor },
            rootExpanded: { background: "transparent", color: theme.colors.fontColor },
            rootDisabled: { background: "transparent", opacity: 0.5 },
            label: { fontFamily: theme.fonts.regular, fontSize: 12, fontWeight: 400, margin: 0 },
            menuIcon: { fontSize: 10, color: "inherit" },
          }}
        />
      ) : (
        <Text
          styles={{
            root: {
              fontFamily: theme.fonts.regular,
              fontSize: 12,
              color: theme.colors.deactiveColor,
              whiteSpace: "nowrap",
              flexShrink: 0,
            },
          }}
        >
          {role}
        </Text>
      )}
    </Stack>
  );
}

/** "People you specify can view", "Anyone with the link can edit", … */
function formatLinkAudience(link) {
  const scope = (link?.scope ?? "").toLowerCase();
  const type = (link?.type ?? "").toLowerCase();
  const who =
    scope === "anonymous"
      ? "Anyone with the link"
      : scope === "organization"
        ? "People in your organization"
        : scope === "users"
          ? "People you specify"
          : "People with the link";
  const can =
    type === "edit" ? "can edit" : type === "view" ? "can view" : (link?.role || "").toLowerCase();
  return can ? `${who} ${can}` : who;
}

function linkRecipientKey(link, person) {
  return `${link.id}|${person.email || person.id}`;
}

/**
 * One sharing link: URL + Copy, a delete (trash) action with inline confirm,
 * the audience line, and — for "Specific people" links — an expandable list of
 * the people it works for, each removable.
 */
function SharingLinkCard({
  link,
  theme,
  removingLinkKey,
  onRemoveLink,
  onRemoveLinkRecipient,
}) {
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const recipients = link.recipients ?? [];
  const isDeleting = removingLinkKey === link.id;
  const isBusy = Boolean(removingLinkKey);
  const divider = theme.colors.dividerColor ?? "#E1DFDD";

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async () => {
    if (!link.webUrl) return;
    setCopied(await copyTextToClipboard(link.webUrl));
  };

  const iconButtonStyles = {
    root: { color: theme.colors.fontColor, width: 32, height: 32, flexShrink: 0 },
    rootHovered: { color: theme.colors.themeDarkColor, backgroundColor: "transparent" },
    rootPressed: { color: theme.colors.themeDarkColor, backgroundColor: "transparent" },
    rootDisabled: { backgroundColor: "transparent", opacity: 0.5 },
  };

  return (
    <Stack
      tokens={{ childrenGap: 8 }}
      styles={{ root: { padding: "12px 4px", borderBottom: `1px solid ${divider}` } }}
    >
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            border: `1px solid ${divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          <Icon iconName="Link" styles={{ root: { fontSize: 14, color: theme.colors.fontColor } }} />
        </div>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            border: `1px solid ${divider}`,
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <input
            readOnly
            value={link.webUrl || ""}
            title={link.webUrl || ""}
            aria-label="Sharing link"
            onFocus={(ev) => ev.target.select()}
            style={{
              flex: 1,
              minWidth: 0,
              border: "none",
              outline: "none",
              background: "transparent",
              color: theme.colors.fontColor,
              fontFamily: theme.fonts.regular,
              fontSize: 12,
              padding: "6px 8px",
              textOverflow: "ellipsis",
            }}
          />
          <button
            type="button"
            onClick={handleCopy}
            disabled={!link.webUrl}
            style={{
              border: "none",
              borderLeft: `1px solid ${divider}`,
              background: "transparent",
              color: copied ? theme.colors.themeDarkColor : theme.colors.fontColor,
              fontFamily: theme.fonts.regular,
              fontSize: 12,
              fontWeight: 600,
              padding: "6px 12px",
              cursor: link.webUrl ? "pointer" : "default",
              whiteSpace: "nowrap",
            }}
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        {isDeleting ? (
          <Spinner size={SpinnerSize.small} styles={{ root: { width: 32 } }} />
        ) : (
          <IconButton
            iconProps={{ iconName: "Delete" }}
            title="Delete link"
            ariaLabel="Delete link"
            disabled={isBusy || typeof onRemoveLink !== "function"}
            onClick={() => setConfirmDelete(true)}
            styles={iconButtonStyles}
          />
        )}
      </Stack>

      <ConfirmModal
        isOpen={confirmDelete}
        type="delete"
        theme={theme}
        title="Delete link?"
        subText="The link will not work for anyone anymore."
        confirmButtonText="Remove"
        isDeleting={isDeleting}
        onConfirm={async () => {
          await onRemoveLink(link);
          setConfirmDelete(false);
        }}
        onCancel={() => setConfirmDelete(false)}
      />

      <Text
        styles={{
          root: {
            marginLeft: 42,
            fontFamily: theme.fonts.regular,
            fontSize: 13,
            color: theme.colors.fontColor,
          },
        }}
      >
        {formatLinkAudience(link)}
      </Text>

      {recipients.length > 0 ? (
        <Stack styles={{ root: { marginLeft: 42 } }}>
          <button
            type="button"
            onClick={() => setRecipientsOpen((open) => !open)}
            aria-expanded={recipientsOpen}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              alignSelf: "flex-start",
              border: "none",
              background: "transparent",
              padding: "2px 0",
              cursor: "pointer",
              color: theme.colors.fontColor,
              fontFamily: theme.fonts.regular,
              fontSize: 12,
              fontWeight: 400,
            }}
          >
            This link works for
            <Icon iconName={recipientsOpen ? "ChevronUp" : "ChevronDown"} styles={{ root: { fontSize: 10 } }} />
          </button>
          {recipientsOpen
            ? recipients.map((person) => {
                const key = linkRecipientKey(link, person);
                const isRemoving = removingLinkKey === key;
                return (
                  <Stack
                    key={key}
                    horizontal
                    verticalAlign="center"
                    tokens={{ childrenGap: 8 }}
                    styles={{ root: { padding: "6px 0" } }}
                  >
                    <Persona
                      text={person.displayName}
                      secondaryText={person.email || undefined}
                      size={PersonaSize.size32}
                      styles={{
                        root: { flex: 1, minWidth: 0 },
                        primaryText: {
                          fontFamily: theme.fonts.regular,
                          fontSize: 13,
                          color: theme.colors.fontColor,
                          selectors: { ":hover": { color: theme.colors.fontColor } },
                        },
                        secondaryText: {
                          fontFamily: theme.fonts.regular,
                          fontSize: 11,
                          color: theme.colors.deactiveColor,
                          selectors: { ":hover": { color: theme.colors.deactiveColor } },
                        },
                      }}
                    />
                    {isRemoving ? (
                      <Spinner size={SpinnerSize.small} styles={{ root: { width: 32 } }} />
                    ) : (
                      <IconButton
                        iconProps={{ iconName: "Cancel" }}
                        title={`Remove ${person.displayName} from this link`}
                        ariaLabel={`Remove ${person.displayName} from this link`}
                        disabled={isBusy || typeof onRemoveLinkRecipient !== "function"}
                        onClick={() => onRemoveLinkRecipient(link, person)}
                        styles={{
                          ...iconButtonStyles,
                          icon: { fontSize: 11 },
                        }}
                      />
                    )}
                  </Stack>
                );
              })
            : null}
        </Stack>
      ) : null}
    </Stack>
  );
}

function EmptyTabMessage({ message, theme }) {
  return (
    <Text
      styles={{
        root: {
          display: "block",
          padding: "20px 8px",
          textAlign: "center",
          fontFamily: theme.fonts.regular,
          fontSize: 13,
          color: theme.colors.deactiveColor,
        },
      }}
    >
      {message}
    </Text>
  );
}

/**
 * Dialog showing OneDrive document sharing status, people, groups, and links.
 */
export function SharingDetailsDialog({
  isOpen,
  onClose,
  loading,
  stopping = false,
  isError,
  message,
  documentName,
  iconSrc,
  isFolder = false,
  details,
  onStopSharing,
  onRemoveLink,
  onRemoveLinkRecipient,
  removingLinkKey = "",
  linkError = "",
  onChangePersonRole,
  updatingPersonKey = "",
}) {
  const theme = useTheme();

  const people = details?.people ?? [];
  const groups = details?.groups ?? [];
  const links = details?.links ?? [];
  const isShared = details?.isShared === true;
  const displayName = details?.documentName || documentName || "Document";
  const showStopSharing =
    !loading && !isError && isShared && typeof onStopSharing === "function";

  const statusLabel = useMemo(() => {
    if (loading || isError || !details) return null;
    return isShared ? "Shared" : "Not shared";
  }, [loading, isError, details, isShared]);

  const titleContent = (
    <Stack tokens={{ childrenGap: 8 }} styles={{ root: { padding: "4px 4px 0" } }}>
      <Text
        styles={{
          root: {
            fontFamily: theme.fonts.regular,
            fontWeight: 600,
            fontSize: 18,
            color: theme.colors.fontColor,
          },
        }}
      >
        Sharing Details
      </Text>
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
        <DocumentTypeIcon
          iconSrc={iconSrc}
          documentName={displayName}
          isShared={!loading && !isError && isShared}
          isFolder={isFolder}
          theme={theme}
        />
        <Text
          styles={{
            root: {
              fontFamily: theme.fonts.regular,
              fontSize: 13,
              color: theme.colors.fontColor,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
          title={displayName}
        >
          {displayName}
        </Text>
      </Stack>
      {(statusLabel || showStopSharing) && (
        <Stack
          horizontal
          verticalAlign="center"
          horizontalAlign="space-between"
          styles={{ root: { width: "100%", marginTop: 4 } }}
        >
          {statusLabel ? (
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 6 }}
              styles={{
                root: {
                  padding: "4px 10px",
                  borderRadius: 4,
                  backgroundColor: isShared
                    ? "rgba(76, 141, 106, 0.15)"
                    : "rgba(96, 94, 92, 0.1)",
                },
              }}
            >
              <Icon
                iconName={isShared ? "People" : "Lock"}
                styles={{
                  root: {
                    fontSize: 12,
                    color: isShared
                      ? theme.colors.themeDarkColor
                      : theme.colors.deactiveColor,
                  },
                }}
              />
              <Text
                styles={{
                  root: {
                    fontFamily: theme.fonts.regular,
                    fontSize: 12,
                    fontWeight: 600,
                    color: isShared
                      ? theme.colors.themeDarkColor
                      : theme.colors.deactiveColor,
                  },
                }}
              >
                {statusLabel}
              </Text>
            </Stack>
          ) : (
            <span />
          )}
          {showStopSharing ? (
            <Button
              onClick={onStopSharing}
              disabled={stopping}
              variant="text"
              startIcon={
                <Icon
                  iconName="SkypeCircleMinus"
                  styles={{
                    root: {
                      fontSize: 14,
                      color: theme.colors.themeDarkColor,
                    },
                  }}
                />
              }
              sx={{
                color: theme.colors.themeDarkColor,
                fontFamily: theme.fonts.regular,
                fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                textTransform: "none",
                minWidth: "auto",
                padding: "2px 6px",
                "&:hover": {
                  backgroundColor: "rgba(36, 159, 93, 0.08)",
                },
                "&.Mui-disabled": {
                  color: theme.colors.deactiveColor,
                },
              }}
            >
              {stopping ? "Stopping…" : "Stop sharing"}
            </Button>
          ) : null}
        </Stack>
      )}
    </Stack>
  );

  const actions = (
    <Stack
      horizontal
      horizontalAlign="end"
      styles={{ root: { padding: "15px 4px 6px" } }}
    >
      <Button
        onClick={onClose}
        variant="outlined"
        sx={{
          color: theme.colors.themeDarkColor,
          borderColor: theme.colors.themeDarkColor,
          fontFamily: theme.fonts.regular,
          fontSize: `${theme.fontSizes?.normal ?? 12}px`,
          textTransform: "none",
          borderRadius: "20px",
          paddingX: 2,
        }}
      >
        Close
      </Button>
    </Stack>
  );

  let bodyContent;
  if (loading) {
    bodyContent = (
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        tokens={{ childrenGap: 12 }}
        styles={{ root: { minHeight: 120, padding: 16 } }}
      >
        <Spinner size={SpinnerSize.medium} label="Loading sharing details…" />
      </Stack>
    );
  } else if (isError && !details) {
    bodyContent = (
      <Stack tokens={{ childrenGap: 8 }} styles={{ root: { padding: "12px 4px" } }}>
        <Text
          styles={{
            root: {
              fontFamily: theme.fonts.regular,
              fontSize: 13,
              color: theme.colors.fontColor,
            },
          }}
        >
          Could not load sharing details for this document.
        </Text>
        {message ? (
          <Text
            styles={{
              root: {
                fontFamily: theme.fonts.regular,
                fontSize: 12,
                color: theme.colors.deactiveColor,
              },
            }}
          >
            {message}
          </Text>
        ) : null}
      </Stack>
    );
  } else if (stopping) {
    bodyContent = (
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        tokens={{ childrenGap: 12 }}
        styles={{ root: { minHeight: 120, padding: 16 } }}
      >
        <Spinner size={SpinnerSize.medium} label="Stopping sharing…" />
      </Stack>
    );
  } else if (!isShared) {
    bodyContent = (
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        tokens={{ childrenGap: 10 }}
        styles={{ root: { minHeight: 140, padding: "24px 12px" } }}
      >
        <Icon
          iconName="Lock"
          styles={{
            root: {
              fontSize: 28,
              color: theme.colors.deactiveColor,
            },
          }}
        />
        <Text
          styles={{
            root: {
              fontFamily: theme.fonts.regular,
              fontSize: 14,
              fontWeight: 600,
              color: theme.colors.fontColor,
            },
          }}
        >
          Not shared
        </Text>
        <Text
          styles={{
            root: {
              fontFamily: theme.fonts.regular,
              fontSize: 12,
              color: theme.colors.deactiveColor,
              textAlign: "center",
              maxWidth: 320,
            },
          }}
        >
          This document is not shared with any people, groups, or links.
          {people.length === 1 && people[0]?.isOwner
            ? ` Only ${people[0].displayName} (Owner) has access.`
            : ""}
        </Text>
      </Stack>
    );
  } else {
    bodyContent = (
      <Pivot
        aria-label="Sharing details tabs"
        styles={{
          root: { marginTop: 4 },
          link: {
            fontFamily: theme.fonts.regular,
            fontSize: 13,
            color: theme.colors.deactiveColor,
            backgroundColor: "transparent",
            selectors: {
              ":hover": {
                color: theme.colors.fontColor,
                backgroundColor: "transparent",
              },
              ":active": {
                color: theme.colors.fontColor,
                backgroundColor: "transparent",
              },
            },
          },
          linkIsSelected: {
            fontFamily: theme.fonts.regular,
            fontSize: 13,
            fontWeight: 600,
            color: theme.colors.fontColor,
            backgroundColor: "transparent",
            selectors: {
              "::before": {
                backgroundColor: theme.colors.addovation20Green,
              },
              ":hover": {
                color: theme.colors.fontColor,
                backgroundColor: "transparent",
              },
              ":active": {
                color: theme.colors.fontColor,
                backgroundColor: "transparent",
              },
            },
          },
        }}
      >
        <PivotItem headerText={`People${people.length ? ` · ${people.length}` : ""}`}>
          {people.length === 0 ? (
            <EmptyTabMessage message="No people have access." theme={theme} />
          ) : (
            <>
              {linkError ? (
                <MessageBar
                  messageBarType={MessageBarType.error}
                  isMultiline
                  styles={{ root: { marginTop: 8 } }}
                >
                  {linkError}
                </MessageBar>
              ) : null}
              {people.map((person) => {
                const personKey = person.email || person.id;
                const canChange =
                  typeof onChangePersonRole === "function" &&
                  !person.isOwner &&
                  // Someone shared "Can edit" from the start has no view grant and
                  // stays Can edit; people given "Can view" can be toggled view/edit.
                  // (A missing flag — older API — keeps the dropdown.)
                  person.hasViewAccess !== false &&
                  Boolean(personKey);
                return (
                  <SharingPrincipalRow
                    key={person.id || person.displayName}
                    name={person.displayName}
                    secondaryText={person.email}
                    role={person.role}
                    theme={theme}
                    onChangeRole={
                      canChange ? (role) => onChangePersonRole(person, role) : undefined
                    }
                    isUpdating={Boolean(personKey) && updatingPersonKey === personKey}
                    disabled={Boolean(updatingPersonKey || removingLinkKey)}
                  />
                );
              })}
            </>
          )}
        </PivotItem>
        <PivotItem headerText={`Groups${groups.length ? ` · ${groups.length}` : ""}`}>
          {groups.length === 0 ? (
            <EmptyTabMessage
              message="This document is not shared with any groups."
              theme={theme}
            />
          ) : (
            groups.map((group) => (
              <SharingPrincipalRow
                key={group.id || group.displayName}
                name={group.displayName}
                role={group.role}
                theme={theme}
              />
            ))
          )}
        </PivotItem>
        <PivotItem headerText={`Links${links.length ? ` · ${links.length}` : ""}`}>
          {links.length === 0 ? (
            <EmptyTabMessage
              message="This document has no sharing links."
              theme={theme}
            />
          ) : (
            <>
              {linkError ? (
                <MessageBar
                  messageBarType={MessageBarType.error}
                  isMultiline
                  styles={{ root: { marginTop: 8 } }}
                >
                  {linkError}
                </MessageBar>
              ) : null}
              {links.map((link) => (
                <SharingLinkCard
                  key={link.id}
                  link={link}
                  theme={theme}
                  removingLinkKey={removingLinkKey}
                  onRemoveLink={onRemoveLink}
                  onRemoveLinkRecipient={onRemoveLinkRecipient}
                />
              ))}
            </>
          )}
        </PivotItem>
      </Pivot>
    );
  }

  return (
    <ReusableDialog
      isOpen={isOpen}
      onClose={onClose}
      showHeader={false}
      variant="info"
      isBlocking={false}
      dialogMinHeight={280}
      dialogMinWidth={420}
      dialogMaxWidth="min(92vw, 520px)"
      dialogMaxHeight="90vh"
      bodyMinHeight={160}
      bodyMaxHeight="60vh"
      titleContent={titleContent}
      actions={actions}
    >
      {bodyContent}
    </ReusableDialog>
  );
}

export default SharingDetailsDialog;
